import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import HirafiIcon from "@/components/HirafiIcon";
import { Lock } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";

type PageState = "loading" | "ready" | "error";

const ResetPassword = () => {
  useSEO({ noindex: true });
  const { t } = useLanguage("common");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState<PageState>("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);

    if (params.get("error")) {
      const description = params.get("error_description") ?? t("reset_password.error_expired");
      setErrorMsg(description.replace(/\+/g, " "));
      setPageState("error");
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setPageState("ready");
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: t("reset_password.toast_error"), description: t("reset_password.toast_mismatch"), variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: t("reset_password.toast_error"), description: t("reset_password.toast_too_short"), variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast({ title: t("reset_password.toast_error"), description: error.message, variant: "destructive" });
    } else {
      toast({ title: t("reset_password.toast_success"), description: t("reset_password.toast_success_desc") });
      lNavigate("/connexion");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <nav className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-md">
        <div className="container flex h-16 items-center">
          <Link to={lp("/")} className="flex items-center gap-2">
            <HirafiIcon size={36} />
            <span className="font-display text-xl font-bold">Hirafi</span>
          </Link>
        </div>
      </nav>

      <main className="flex flex-1 items-center justify-center px-5 py-16">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <h1 className="font-display text-2xl font-bold">{t("reset_password.title")}</h1>
            <p className="mt-1.5 text-muted-foreground">{t("reset_password.subtitle")}</p>
          </div>

          {pageState === "loading" && (
            <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
              {t("reset_password.checking")}
            </div>
          )}

          {pageState === "error" && (
            <div className="rounded-xl border bg-card p-6 space-y-4 text-center">
              <p className="text-sm font-medium text-destructive">{errorMsg}</p>
              <p className="text-sm text-muted-foreground">{t("reset_password.error_expired")}</p>
              <Link to={lp("/connexion")}>
                <Button variant="hero" className="w-full">
                  {t("reset_password.back_to_login")}
                </Button>
              </Link>
            </div>
          )}

          {pageState === "ready" && (
            <form onSubmit={handleSubmit} className="space-y-5 rounded-xl border bg-card p-6">
              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="font-display font-semibold text-sm">
                  {t("reset_password.label_new")}
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="font-display font-semibold text-sm">
                  {t("reset_password.label_confirm")}
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="h-11"
                />
              </div>
              <Button variant="hero" size="xl" className="w-full" type="submit" disabled={loading}>
                {loading ? t("reset_password.submitting") : t("reset_password.submit")}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
